import { $createLineBreakNode, $createParagraphNode, $createTextNode, $getRoot, createEditor, TextNode } from "lexical";
import type { EditorState, SerializedEditorState } from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { UNORDERED_LIST, ORDERED_LIST } from "@lexical/markdown";
import { logger } from "../../../../common/util/logger";

// Shared Lexical config for the notes editor and the read-only viewer so both
// render with identical formatting.
// These values are css class names that control the styling for each element
export const notesTheme = {
  paragraph: "editor-paragraph",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
  },
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
  },
  list: {
    ul: "editor-list-ul",
    ol: "editor-list-ol",
    listitem: "editor-listitem",
  },
};

export const NOTE_NODES = [TextNode, HeadingNode, QuoteNode, ListNode, ListItemNode];

// Markdown shortcuts: typing "- " or "* " starts a bullet list, "1. " starts an ordered list.
export const LIST_TRANSFORMERS = [UNORDERED_LIST, ORDERED_LIST];

export function notesOnError(error: Error) {
  logger.error("Notes error", error);
}

// Use Lexical itself to interpret text, including its block and list separators.
// Throw from onError so malformed or unsupported serialized states fall back to plain text.
const noteParser = createEditor({
  namespace: "NoteContentParser",
  nodes: NOTE_NODES,
  onError: (error) => {
    throw error;
  },
});

const isSerializedNode = (value: unknown, parent: string | null): boolean => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const node = value as Record<string, unknown>;
  if (typeof node.type !== "string" || node.version !== 1) return false;
  const type = node.type;
  if (parent === null && type !== "root") return false;
  if (parent !== null && type === "root") return false;
  if (parent === "root" && !["paragraph", "heading", "quote", "list"].includes(type)) return false;
  if (parent === "list" && type !== "listitem") return false;
  if (
    (parent === "paragraph" || parent === "heading" || parent === "quote") &&
    type !== "text" &&
    type !== "tab" &&
    type !== "linebreak"
  )
    return false;
  if (parent === "listitem" && type !== "text" && type !== "tab" && type !== "linebreak" && type !== "list") {
    return false;
  }
  if (type === "text" || type === "tab") {
    if (type === "tab" && (node.text !== "\t" || node.detail !== 2 || node.mode !== "normal")) return false;
    return (
      typeof node.text === "string" &&
      typeof node.format === "number" &&
      typeof node.detail === "number" &&
      typeof node.style === "string" &&
      (node.mode === "normal" || node.mode === "token" || node.mode === "segmented")
    );
  }
  if (type === "linebreak") return true;
  if (!["root", "paragraph", "heading", "quote", "list", "listitem"].includes(type)) return false;
  if (
    !Array.isArray(node.children) ||
    typeof node.indent !== "number" ||
    typeof node.format !== "string" ||
    !(node.direction === null || node.direction === "ltr" || node.direction === "rtl")
  )
    return false;
  if (type === "heading" && !["h1", "h2", "h3", "h4", "h5", "h6"].includes(String(node.tag))) return false;
  if (
    type === "list" &&
    (!(node.listType === "bullet" || node.listType === "number" || node.listType === "check") ||
      typeof node.start !== "number")
  )
    return false;
  if (type === "listitem" && typeof node.value !== "number") return false;
  return node.children.every((child: unknown) => isSerializedNode(child, type));
};

export const parseLexicalNote = (content: string | undefined): EditorState | null => {
  if (!content) return null;
  try {
    const parsed: unknown = JSON.parse(content);
    if (typeof parsed !== "object" || parsed === null || !("root" in parsed) || !isSerializedNode(parsed.root, null)) {
      return null;
    }
    const state = noteParser.parseEditorState(parsed as SerializedEditorState);
    if (!state.isEmpty()) return state;
    // LexicalComposer cannot mount a root-only state; seed an empty paragraph.
    noteParser.update(
      () => {
        $getRoot().clear().append($createParagraphNode());
      },
      { discrete: true },
    );
    return noteParser.getEditorState();
  } catch {
    return null;
  }
};

export const parseNoteContent = (content: string | undefined): SerializedEditorState | null => {
  if (!content) return null;
  const state = parseLexicalNote(content);
  if (state) {
    const parsed = JSON.parse(content) as SerializedEditorState;
    return parsed.root.children.length ? parsed : state.toJSON();
  }

  // A single paragraph with explicit line breaks round-trips plain text, including
  // consecutive and trailing newlines, without turning it into Markdown.
  noteParser.update(
    () => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      content.split("\n").forEach((line, index) => {
        if (index > 0) paragraph.append($createLineBreakNode());
        if (line) paragraph.append($createTextNode(line));
      });
      root.append(paragraph);
    },
    { discrete: true },
  );
  return noteParser.getEditorState().toJSON();
};
